<template>
  <main class="mx-auto pt-16">
    <CommonBackToButton
      text-to="About Us"
      url="/about"
      class="container mx-auto pt-5 md:pt-12"
    />

    <div class="mt-6">
      <div class="container mx-auto mb-8">
        <h1
          class="text-[22px] leading-normal sm:text-[40px] sm:leading-[50px] font-medium mb-4 text-bw-900"
        >
          Our Remarkable Journey through the Years
        </h1>
      </div>

      <section class="">
        <div v-if="isLoading" class="w-full p-5 border-t border-[#bbbbbb]">
          <div
            v-for="idx in 2"
            :key="idx"
            class="w-full flex justify-end gap-10 mb-10"
          >
            <div class="w-2/5">
              <UtilsSkeletonLoading class="w-full h-36" />
              <UtilsSkeletonLoading class="w-1/3 h-6 mt-2" />
            </div>
            <div class="w-2/5 h-40 flex flex-col justify-center gap-2">
              <div v-for="i in 3" :key="i" class="w-full flex gap-4">
                <UtilsSkeletonLoading class="w-9 h-7" />
                <UtilsSkeletonLoading class="w-4/5 h-7" />
              </div>
            </div>
          </div>
        </div>
        <Swiper
          v-else
          :modules="[SwiperMousewheel]"
          :slides-per-view="`auto`"
          :mousewheel="{
            releaseOnEdges: true
          }"
          :direction="`vertical`"
          class=""
        >
          <SwiperSlide
            v-for="(slide, index) in journeysData"
            :key="index"
            class="!flex timeline relative"
          >
            <span
              class="absolute top-1/4 -left-[1.2rem] text-[#E8E8E8] text-9xl leading-normal font-medium tracking-[1px] opacity-40"
              >{{ slide.attributes?.year }}</span
            >
            <div class="container mx-auto flex justify-between">
              <div class="w-[15%] md:w-[10%] year text-lg text-primary-primary">
                {{ slide.attributes?.year }}
              </div>
              <div
                class="flex justify-between flex-col md:flex-row py-8 z-[1] timeline--image-month"
              >
                <div
                  class="w-full px-10 md:px-0 md:w-1/2 my-auto ml-0 md:ml-[50px]"
                >
                  <NuxtImg
                    format="webp"
                    :src="slide.attributes?.image?.data?.[0]?.attributes?.url"
                    :alt="slide.attributes?.image_description"
                    :title="slide.attributes?.image_description"
                    loading="lazy"
                    class="w-auto md:w-2/3 mx-auto"
                  />
                  <p
                    class="text-[#4B4B4B] text-xs leading-5 pl-5 w-auto md:w-2/3 mx-auto"
                  >
                    {{ slide.attributes?.image_description }}
                  </p>
                </div>
                <div class="w-full px-10 md:px-0 md:w-1/2 md:my-auto mt-5">
                  <div
                    v-for="(item, idx) in slide.attributes.months"
                    :key="idx"
                    class="grid grid-cols-6 lg:grid-cols-12 mb-2 gap-3"
                  >
                    <p
                      class="text-orange-500 mr-2 font-medium col-span-1 md:col-span-1 text-sm md:text-base"
                    >
                      {{ getMonthName(item.month) }}
                    </p>
                    <p class="col-span-5 lg:col-span-11 text-sm md:text-base">
                      {{ item.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </section>
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const route = useRoute()
const isLoading = ref(true)
const journeysData = ref([])
const SEOMeta = ref({})

const [journeys, seo] = await Promise.all([
  useSuitmediaApiData(`/api/journeys`, {
    query: {
      'fields[0]': 'id',
      'fields[1]': 'year',
      'fields[2]': 'image_description',
      populate: 'image,months',
      'sort[0]': 'year:desc'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value = journeys.pending.value

if (journeys.error.value) {
  throw createError({
    statusCode: 404,
    message: `Histories does not exist`
  })
}

journeysData.value = journeys.data.value.data
SEOMeta.value = seo.data.value.data[0]

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
</script>

<style lang="scss" scoped>
:deep(.swiper) {
  border-top: 1px solid #bbbbbb;
}
:deep(.swiper-wrapper) {
  height: 70vh;
}
:deep(.swiper-slide) {
  min-height: 200px;
  height: auto;
}

.timeline {
  position: relative;
  margin: 0 auto;

  .timeline--image-month {
    width: -moz-available;
    width: -webkit-fill-available;
  }
}

.year {
  position: relative;
  padding-top: 32px;
}
.year::before {
  content: '';
  position: absolute;
  width: 1px;
  background-color: #bbbbbb;
  top: 0;
  bottom: 0;
  left: 67.2px;
}
.year::after {
  content: '';
  position: absolute;
  width: 22px;
  height: 22px;
  top: 36px;
  left: 57px;
  background-color: #ff6700;
  border: 6px solid #fee0cc;
  border-radius: 50%;
  z-index: 1;
}
</style>
